<script>
import axios from "axios";
import Layout from "../../layouts/auth";
import appConfig from "@/app.config";
import { required, minLength, helpers } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";

export default {
  setup() {
    return { v$: useVuelidate() };
  },
  page: {
    title: "Set Password",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: {
    Layout,
  },
  data() {
    return {
      password: "",
      confirmPassword: "",
      showPassword: false,
      showConfirm: false,
      submitted: false,
      isSuccess: false,
      isError: false,
      errorMessage: "",
      successMessage: "",
      isSubmitting: false,
      isActivation: false,
    };
  },
  validations: {
    password: {
      required: helpers.withMessage("Password is required", required),
      minLength: helpers.withMessage(
        "Password must be at least 8 characters",
        minLength(8),
      ),
    },
    confirmPassword: {
      required: helpers.withMessage("Confirm password is required", required),
    },
  },
  computed: {
    loginPath() {
      return this.isActivation ? "/staff_login" : "/login";
    },
  },
  created() {
    this.isActivation = this.$route.path.includes("set-password");
  },
  methods: {
    handleSubmit() {
      this.submitted = true;
      this.v$.$touch();
      if (this.v$.$invalid) return;

      if (this.password !== this.confirmPassword) {
        this.isError = true;
        this.errorMessage = "Passwords do not match";
        return;
      }

      const uid = this.$route.query.uid;
      const token = this.$route.query.token;

      if (!uid || !token) {
        this.isError = true;
        this.errorMessage = "Invalid or expired link parameters";
        return;
      }

      this.isSubmitting = true;
      this.isError = false;

      const endpoint = this.isActivation
        ? "/accounts/set_staff_password/"
        : "/accounts/reset_password_confirm/";

      const payload = this.isActivation
        ? { uid, token, password: this.password }
        : { uid, token, new_password: this.password };

      axios
        .post(endpoint, payload)
        .then((response) => {
          this.isSubmitting = false;
          this.isSuccess = true;
          this.successMessage =
            response.data.msg || "Password updated successfully.";
          setTimeout(() => {
            this.$router.push(this.loginPath);
          }, 2000);
        })
        .catch((error) => {
          this.isSubmitting = false;
          this.isError = true;
          this.errorMessage =
            error.response?.data?.error ||
            "Failed to update password. Link may be expired.";
        });
    },
  },
};
</script>

<template>
  <Layout>
    <div class="eauth-shell">
      <div class="eauth-card">
        <img
          class="eauth-logo"
          src="@/assets/images/ECU/ECU-logo.jpg"
          alt="Eastern Credit Union"
        />
        <div class="eauth-accent"></div>

        <!-- Success state -->
        <div v-if="isSuccess" class="eauth-status">
          <div class="eauth-status__icon"><i class="mdi mdi-check"></i></div>
          <h1 class="eauth-title">All Set!</h1>
          <p class="eauth-sub">{{ successMessage }} Redirecting you to login...</p>
          <router-link :to="loginPath" class="eauth-btn eauth-btn--primary eauth-btn--block">
            Go to Login <i class="mdi mdi-arrow-right"></i>
          </router-link>
        </div>

        <form v-else @submit.prevent="handleSubmit" novalidate>
          <h1 class="eauth-title">
            {{ isActivation ? "Set Your Password" : "Reset Your Password" }}
          </h1>
          <p class="eauth-sub">
            {{
              isActivation
                ? "Create a password to activate your staff account."
                : "Choose a new password for your Eastern Credit Union account."
            }}
          </p>

          <div v-if="isError" class="eauth-alert eauth-alert--error" role="alert">
            <i class="mdi mdi-alert-circle-outline"></i>
            <span>{{ errorMessage }}</span>
          </div>

          <div class="eauth-group">
            <label class="eauth-label" for="new_password">New Password</label>
            <div
              class="eauth-field"
              :class="{ 'is-invalid': submitted && v$.password.$error }"
            >
              <i class="mdi mdi-lock-outline eauth-field__icon"></i>
              <input
                :type="showPassword ? 'text' : 'password'"
                v-model="password"
                id="new_password"
                class="eauth-input"
                placeholder="Enter new password (min. 8 characters)"
                autocomplete="new-password"
              />
              <button
                type="button"
                class="eauth-field__toggle"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                @click="showPassword = !showPassword"
              >
                <i :class="showPassword ? 'mdi mdi-eye-off-outline' : 'mdi mdi-eye-outline'"></i>
              </button>
            </div>
            <div
              v-for="(item, index) in v$.password.$errors"
              :key="index"
              class="eauth-error"
            >
              <span>{{ item.$message }}</span>
            </div>
          </div>

          <div class="eauth-group">
            <label class="eauth-label" for="confirm_password">Confirm Password</label>
            <div
              class="eauth-field"
              :class="{ 'is-invalid': submitted && v$.confirmPassword.$error }"
            >
              <i class="mdi mdi-lock-check-outline eauth-field__icon"></i>
              <input
                :type="showConfirm ? 'text' : 'password'"
                v-model="confirmPassword"
                id="confirm_password"
                class="eauth-input"
                placeholder="Re-enter new password"
                autocomplete="new-password"
              />
              <button
                type="button"
                class="eauth-field__toggle"
                :aria-label="showConfirm ? 'Hide password' : 'Show password'"
                @click="showConfirm = !showConfirm"
              >
                <i :class="showConfirm ? 'mdi mdi-eye-off-outline' : 'mdi mdi-eye-outline'"></i>
              </button>
            </div>
            <div
              v-for="(item, index) in v$.confirmPassword.$errors"
              :key="index"
              class="eauth-error"
            >
              <span>{{ item.$message }}</span>
            </div>
          </div>

          <div class="eauth-actions">
            <router-link :to="loginPath" class="eauth-btn eauth-btn--ghost">
              <i class="mdi mdi-arrow-left"></i> Back
            </router-link>
            <button
              class="eauth-btn eauth-btn--primary"
              type="submit"
              :disabled="isSubmitting"
            >
              <span v-if="isSubmitting">Processing...</span>
              <template v-else>
                {{ isActivation ? "Activate Account" : "Reset Password" }}
                <i class="mdi mdi-arrow-right"></i>
              </template>
            </button>
          </div>
        </form>
      </div>

      <div class="eauth-footer">
        <p>
          © {{ new Date().getFullYear() }} ECU. Crafted with by
          <a target="_blank" rel="noopener" href="https://midriffinfosolution.org/">MISPL</a>
        </p>
      </div>
    </div>
  </Layout>
</template>